import { Order, Trip, Waypoint } from "@/api.types";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  UseMutationOptions,
} from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { useSession } from "@/services/auth0/session";
import { z } from "zod";
import { taskQueries } from "./tasks";

export interface TripsResponse {
  data: Trip[];
  metadata: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

interface UseTripsOptions {
  status?: string;
  limit?: number;
  page?: number;
}

export function useTrips(options: UseTripsOptions = {}) {
  const { token } = useSession();

  return useQuery<TripsResponse>({
    queryKey: ["trips", options],
    queryFn: async () => {
      const { data } = await axios.get<TripsResponse>(
        `${import.meta.env.VITE_API_URL}/api/v1/trips`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            status: options.status,
            limit: options.limit,
            page: options.page,
          },
        }
      );
      return data;
    },
    enabled: !!token,
  });
}

// Convenience hook for active trips
export function useActiveTrips() {
  return useTrips({ status: "PLANNED,ACTIVE,COMPLETED" });
}

export type CreateTripFormSchema = z.infer<typeof createTripSchema>;
export const createTripSchema = z.object({
  depotId: z.string().nonempty("Depot is required"),
  color: z.string().optional(),
});

export function useCreateTrip({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: CreateTripFormSchema) => {
      const { data } = await axios.post<Trip>(
        `${import.meta.env.VITE_API_URL}/api/v1/depots/${values.depotId}/trips/create`,
        values,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["trips", { status: "PLANNED,ACTIVE,COMPLETED" }],
      });
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
}

export function useTripById(tripId: string) {
  const { token } = useSession();
  return useQuery<Trip>({
    queryKey: ["trips", tripId],
    queryFn: async () => {
      const { data } = await axios.get<Trip>(
        `${import.meta.env.VITE_API_URL}/api/v1/trips/${tripId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    enabled: !!token && !!tripId,
  });
}

export function useAllTripWaypoints({ trips }: { trips?: Trip[] }) {
  const { token } = useSession();
  return useQuery<Waypoint[]>({
    queryKey: ["all-waypoints"],
    queryFn: async () => {
      if (!trips?.length) return [];

      // Fetch waypoints for all trips in parallel
      const waypointsPromises = trips.map((trip) =>
        axios
          .get<Waypoint[]>(
            `${import.meta.env.VITE_API_URL}/api/v1/trips/${trip.id}/waypoints`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          )
          .then((response) => response.data)
      );

      const results = await Promise.all(waypointsPromises);
      return results.flat(); // Flatten array of arrays into single array
    },
    enabled: !!trips?.length && !!token,
  });
}

export function useDeleteTrip() {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tripId: string) => {
      await axios.delete(
        `${import.meta.env.VITE_API_URL}/api/v1/trips/${tripId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["trips", { status: "PLANNED,ACTIVE,COMPLETED" }],
      });
    },
  });
}

export function useTripWaypoints(trips?: Trip[]) {
  const { token } = useSession();
  return useQueries({
    queries: (trips ?? []).map((trip) => ({
      queryKey: ["waypoints", trip.id],
      queryFn: async () => {
        const { data } = await axios.get<Waypoint[]>(
          `${import.meta.env.VITE_API_URL}/api/v1/trips/${trip.id}/waypoints`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        return data;
      },
    })),
  });
}

export function useTripWaypointsReorder(
  tripId: string,
  {
    onSuccess,
    onError,
  }: {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
  }
) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      waypointOrder: {
        waypointId: string;
        sequence: number;
      }[]
    ) => {
      const { data } = await axios.patch<Waypoint[]>(
        `${import.meta.env.VITE_API_URL}/api/v1/trips/${tripId}/waypoints/reorder`,
        { waypointOrders: waypointOrder },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["waypoints", tripId] });
      await queryClient.invalidateQueries({ queryKey: ["trips", tripId] });
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
}

export function useAssignOperatorToTrip(
  tripId: string,
  {
    onSuccess,
    onError,
  }: {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
  }
) {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (operatorId: string) => {
      await axios.patch(
        `${import.meta.env.VITE_API_URL}/api/v1/trips/${tripId}/operator/assign`,
        { operatorId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["trips", tripId] });
      onSuccess?.();
    },
    onError: (error) => {
      console.error("Error assigning operator:", error);
      onError?.(error);
    },
  });
}

export function useDeleteTripWaypoint(
  tripId: string,
  {
    onSuccess,
    onError,
  }: {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
  }
) {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (waypointId: string) => {
      const { data } = await axios.delete<{
        message: string;
        tripIds: string[];
        waypointIds: string[];
      }>(
        `${import.meta.env.VITE_API_URL}/api/v1/trips/${tripId}/waypoints/${waypointId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    onSuccess: async (data) => {
      if (data.waypointIds.length > 0) {
        const waypointIds = data.waypointIds;
        await Promise.all(
          waypointIds.map((waypointId) =>
            queryClient.invalidateQueries({
              queryKey: ["waypoints", waypointId],
            })
          )
        );
      }
      if (data.tripIds.length > 0) {
        const tripIds = data.tripIds;
        await Promise.all(
          tripIds.map((tripId) =>
            queryClient.invalidateQueries({ queryKey: ["trips", tripId] })
          )
        );
      }

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: taskQueries.activePickupTasks(),
        }),
        queryClient.invalidateQueries({
          queryKey: taskQueries.activeDeliveryTasks(),
        }),
        queryClient.invalidateQueries({ queryKey: ["all-waypoints"] }),
      ]);
      onSuccess?.();
    },
    onError: (error) => {
      console.error("Error deleting waypoint:", error);
      onError?.(error);
    },
  });
}

export function useArchiveTrip(
  tripId: string,
  {
    onSuccess,
    onError,
  }: {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await axios.patch(
        `${import.meta.env.VITE_API_URL}/api/v1/trips/${tripId}/archive`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["trips"] });
      await queryClient.invalidateQueries({ queryKey: ["trips", tripId] });
      await queryClient.invalidateQueries({ queryKey: ["waypoints", tripId] });
      await queryClient.invalidateQueries({ queryKey: ["all-waypoints"] });
      onSuccess?.();
    },
    onError: (error) => {
      console.error("Error completing trip:", error);
      onError?.(error);
    },
  });
}

export const useUpdateTripRoutingDetails = (
  tripId: string,
  options?: UseMutationOptions<
    Trip,
    AxiosError,
    {
      useCustomStartLocation: boolean;
      useCustomEndLocation: boolean;
      startLocation: Record<string, any> | null;
      endLocation: Record<string, any> | null;
    }
  >
) => {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation<
    Trip,
    AxiosError,
    {
      useCustomStartLocation: boolean;
      useCustomEndLocation: boolean;
      startLocation: Record<string, any> | null;
      endLocation: Record<string, any> | null;
    }
  >({
    mutationFn: async (data) => {
      const response = await axios.patch<Trip>(
        `${import.meta.env.VITE_API_URL}/api/v1/trips/${tripId}/routing-details`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    onSuccess: async (data, variables, context) => {
      // Invalidate trip query to refetch with new data
      await queryClient.invalidateQueries({ queryKey: ["trip", tripId] });

      // Call the onSuccess callback if provided
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      if (options?.onError) {
        options.onError(error, variables, context);
      }
    },
  });
};

// Types for route computation
export interface ComputeRouteResponse {
  tripId: string;
  message: string;
  startTime: string; // ISO string of the actual start time used
}

export interface ComputeRouteParams {
  tripId: string; // Required parameter
  customStartTime?: string; // ISO string format for custom start time
}

export function useComputeRoute({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: ComputeRouteResponse) => void;
  onError?: (error: Error) => void;
} = {}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation<ComputeRouteResponse, Error, ComputeRouteParams>({
    mutationFn: async (params: ComputeRouteParams) => {
      const { tripId, customStartTime } = params;

      const { data } = await axios.get<ComputeRouteResponse>(
        `${import.meta.env.VITE_API_URL}/api/v1/trips/${tripId}/route/compute`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { customStartTime }, // Only include customStartTime in the query params
        }
      );
      return data;
    },
    onSuccess: async (data) => {
      const { tripId } = data;
      // Invalidate relevant queries after route computation
      await queryClient.invalidateQueries({ queryKey: ["trips", tripId] });
      await queryClient.invalidateQueries({ queryKey: ["waypoints", tripId] });
      await queryClient.invalidateQueries({ queryKey: ["all-waypoints"] });
      onSuccess?.(data);
    },
    onError: (error) => {
      console.error("Error computing route:", error);
      onError?.(error);
    },
  });
}

// Add interface for pickup operators response
export interface PickupOperator {
  operator: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  senderGroups: {
    senderName: string;
    orders: {
      id: string;
      orderNumber: string;
      senderName?: string;
    }[];
  }[];
}

export interface PickupOperatorsResponse {
  operators: PickupOperator[];
}

// Add function to get pickup operators for a trip
export function useTripPickupOperators(tripId: string) {
  const { token } = useSession();
  return useQuery<PickupOperatorsResponse>({
    queryKey: ["trip-pickup-operators", tripId],
    queryFn: async () => {
      const { data } = await axios.get<PickupOperatorsResponse>(
        `${import.meta.env.VITE_API_URL}/api/v1/trips/${tripId}/pickup-operators`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    enabled: !!token && !!tripId,
  });
}

export interface TripAnalyticsResponse {
  analytics: {
    totalOrders: number;
    totalParcels: number;
    totalDistance: number;
    totalPickupTasks: number;
    totalDeliveryTasks: number;
    totalDuration: number;
    taskCompletion: {
      completed: number;
      failed: number;
      pending: number;
      total: number;
    };
  };
  orders: Order[];
  tripInfo: {
    id: string;
    tripNumber: string;
    status: string;
    createdAt: string;
    startTime: string;
    endTime: string;
    color: string;
  };
}
export const useTripAnalytics = (tripId: string | null) => {
  const { token } = useSession();
  return useQuery({
    queryKey: ["trip-analytics", tripId],
    queryFn: async () => {
      if (!tripId) return null;
      const { data } = await axios.get<TripAnalyticsResponse>(
        `${import.meta.env.VITE_API_URL}/api/v1/trips/${tripId}/analytics`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    enabled: !!tripId && !!token,
  });
};
